import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_EDITOR_EDITABLE, NOTEBOOK_CELL_EDITABLE, NOTEBOOK_OUTPUT_FOCUSED } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';
import { getNotebookEditorFromEditorPane, expandCellRangesWithHiddenCells, cellRangeToViewCells } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { CopyAction, PasteAction, CutAction } from 'vscode/vscode/vs/editor/contrib/clipboard/browser/clipboard';
import { IClipboardService } from 'vscode/vscode/vs/platform/clipboard/common/clipboardService.service';
import { cloneNotebookCellTextModel } from 'vscode/vscode/vs/workbench/contrib/notebook/common/model/notebookCellTextModel';
import { SelectionStateType } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { INotebookService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookService.service';
import { isNative } from 'vscode/vscode/vs/base/common/platform';
import { registerAction2, MenuId, Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { NotebookCellAction, NotebookAction, NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT, NOTEBOOK_OUTPUT_WEBVIEW_ACTION_WEIGHT } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/coreActions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { InputFocusedContextKey } from 'vscode/vscode/vs/platform/contextkey/common/contextkeys';
import { UndoCommand, RedoCommand } from 'vscode/vscode/vs/editor/browser/editorExtensions';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { showWindowLogActionId } from 'vscode/vscode/vs/workbench/services/log/common/logConstants';
import { getWindow, getActiveElement, isAncestor } from 'vscode/vscode/vs/base/browser/dom';

const _moduleId = "vs/workbench/contrib/notebook/browser/contrib/clipboard/notebookClipboard";
let _logging = false;
function toggleLogging() {
    _logging = !_logging;
}
function _log(loggerService, str) {
    if (_logging) {
        loggerService.info(`[NotebookClipboard]: ${str}`);
    }
}
function getFocusedEditor(accessor) {
    const loggerService = accessor.get(ILogService);
    const editorService = accessor.get(IEditorService);
    const editor = getNotebookEditorFromEditorPane(editorService.activeEditorPane);
    if (!editor) {
        _log(loggerService, '[Revive Webview] No notebook editor found for active editor pane, bypass');
        return;
    }
    if (!editor.hasEditorFocus()) {
        _log(loggerService, '[Revive Webview] Notebook editor is not focused, bypass');
        return;
    }
    if (!editor.hasWebviewFocus()) {
        _log(loggerService, '[Revive Webview] Notebook editor backlayer webview is not focused, bypass');
        return;
    }
    const view = editor.getViewModel();
    if (view && view.viewCells.every(cell => !cell.outputIsFocused && !cell.outputIsHovered)) {
        return;
    }
    return { editor, loggerService };
}
function getFocusedWebviewDelegate(accessor) {
    const result = getFocusedEditor(accessor);
    if (!result) {
        return;
    }
    const webview = result.editor.getInnerWebview();
    _log(result.loggerService, '[Revive Webview] Notebook editor backlayer webview is focused');
    return webview;
}
function withWebview(accessor, f) {
    const webview = getFocusedWebviewDelegate(accessor);
    if (webview) {
        f(webview);
        return true;
    }
    return false;
}
function withEditor(accessor, f) {
    const result = getFocusedEditor(accessor);
    return result ? f(result.editor) : false;
}
const PRIORITY = 105;
UndoCommand.addImplementation(PRIORITY, 'notebook-webview', accessor => {
    return withWebview(accessor, webview => webview.undo());
});
RedoCommand.addImplementation(PRIORITY, 'notebook-webview', accessor => {
    return withWebview(accessor, webview => webview.redo());
});
CopyAction?.addImplementation(PRIORITY, 'notebook-webview', accessor => {
    return withWebview(accessor, webview => webview.copy());
});
PasteAction?.addImplementation(PRIORITY, 'notebook-webview', accessor => {
    return withWebview(accessor, webview => webview.paste());
});
CutAction?.addImplementation(PRIORITY, 'notebook-webview', accessor => {
    return withWebview(accessor, webview => webview.cut());
});
function runPasteCells(editor, activeCell, pasteCells) {
    if (!editor.hasModel()) {
        return false;
    }
    const textModel = editor.textModel;
    if (editor.isReadOnly) {
        return false;
    }
    const originalState = {
        kind: SelectionStateType.Index,
        focus: editor.getFocus(),
        selections: editor.getSelections()
    };
    if (activeCell) {
        const currCellIndex = editor.getCellIndex(activeCell);
        const newFocusIndex = typeof currCellIndex === 'number' ? currCellIndex + 1 : 0;
        textModel.applyEdits([
            {
                editType: 1 ,
                index: newFocusIndex,
                count: 0,
                cells: ( (pasteCells.items.map(cell => cloneNotebookCellTextModel(cell))))
            }
        ], true, originalState, () => ({
            kind: SelectionStateType.Index,
            focus: { start: newFocusIndex, end: newFocusIndex + 1 },
            selections: [{ start: newFocusIndex, end: newFocusIndex + pasteCells.items.length }]
        }), undefined, true);
    }
    else {
        if (editor.getLength() !== 0) {
            return false;
        }
        textModel.applyEdits([
            {
                editType: 1 ,
                index: 0,
                count: 0,
                cells: ( (pasteCells.items.map(cell => cloneNotebookCellTextModel(cell))))
            }
        ], true, originalState, () => ({
            kind: SelectionStateType.Index,
            focus: { start: 0, end: 1 },
            selections: [{ start: 1, end: pasteCells.items.length + 1 }]
        }), undefined, true);
    }
    return true;
}
function runCopyCells(accessor, editor, targetCell) {
    if (!editor.hasModel()) {
        return false;
    }
    if (editor.hasOutputTextSelection()) {
        getWindow(editor.getDomNode()).document.execCommand('copy');
        return true;
    }
    const clipboardService = accessor.get(IClipboardService);
    const notebookService = accessor.get(INotebookService);
    const selections = editor.getSelections();
    if (targetCell) {
        const targetCellIndex = editor.getCellIndex(targetCell);
        const containingSelection = selections.find(selection => selection.start <= targetCellIndex && targetCellIndex < selection.end);
        if (!containingSelection) {
            clipboardService.writeText(targetCell.getText());
            notebookService.setToCopy([targetCell.model], true);
            return true;
        }
    }
    const selectionRanges = expandCellRangesWithHiddenCells(editor, editor.getSelections());
    const selectedCells = cellRangeToViewCells(editor, selectionRanges);
    if (!selectedCells.length) {
        return false;
    }
    clipboardService.writeText(( (selectedCells.map(cell => cell.getText()))).join('\n'));
    notebookService.setToCopy(( (selectedCells.map(cell => cell.model))), true);
    return true;
}
function runCutCells(accessor, editor, targetCell) {
    if (!editor.hasModel() || editor.isReadOnly) {
        return false;
    }
    const textModel = editor.textModel;
    const clipboardService = accessor.get(IClipboardService);
    const notebookService = accessor.get(INotebookService);
    const selections = editor.getSelections();
    if (targetCell) {
        const targetCellIndex = editor.getCellIndex(targetCell);
        const containingSelection = selections.find(selection => selection.start <= targetCellIndex && targetCellIndex < selection.end);
        if (!containingSelection) {
            clipboardService.writeText(targetCell.getText());
            const focus = editor.getFocus();
            const newFocus = focus.end <= targetCellIndex ? focus : { start: focus.start - 1, end: focus.end - 1 };
            const newSelections = ( (selections.map(
                selection => (selection.end <= targetCellIndex ? selection : { start: selection.start - 1, end: selection.end - 1 })
            )));
            textModel.applyEdits([
                { editType: 1 , index: targetCellIndex, count: 1, cells: [] }
            ], true, { kind: SelectionStateType.Index, focus: editor.getFocus(), selections: selections }, () => ({ kind: SelectionStateType.Index, focus: newFocus, selections: newSelections }), undefined, true);
            notebookService.setToCopy([targetCell.model], false);
            return true;
        }
    }
    const focus = editor.getFocus();
    const containingSelection = selections.find(selection => selection.start <= focus.start && focus.end <= selection.end);
    if (!containingSelection) {
        const targetCell = editor.cellAt(focus.start);
        clipboardService.writeText(targetCell.getText());
        const newFocus = focus.end === editor.getLength() ? { start: focus.start - 1, end: focus.end - 1 } : focus;
        const newSelections = ( (selections.map(
            selection => (selection.end <= focus.start ? selection : { start: selection.start - 1, end: selection.end - 1 })
        )));
        textModel.applyEdits([
            { editType: 1 , index: focus.start, count: 1, cells: [] }
        ], true, { kind: SelectionStateType.Index, focus: editor.getFocus(), selections: selections }, () => ({ kind: SelectionStateType.Index, focus: newFocus, selections: newSelections }), undefined, true);
        notebookService.setToCopy([targetCell.model], false);
        return true;
    }
    const selectionRanges = expandCellRangesWithHiddenCells(editor, editor.getSelections());
    const selectedCells = cellRangeToViewCells(editor, selectionRanges);
    if (!selectedCells.length) {
        return false;
    }
    clipboardService.writeText(( (selectedCells.map(cell => cell.getText()))).join('\n'));
    const edits = ( (selectionRanges.map(
        range => ({ editType: 1 , index: range.start, count: range.end - range.start, cells: [] })
    )));
    const firstSelectIndex = selectionRanges[0].start;
    const newFocusedCellIndex = firstSelectIndex < textModel.cells.length - 1
        ? firstSelectIndex
        : Math.max(textModel.cells.length - 2, 0);
    textModel.applyEdits(edits, true, { kind: SelectionStateType.Index, focus: editor.getFocus(), selections: selectionRanges }, () => {
        return {
            kind: SelectionStateType.Index,
            focus: { start: newFocusedCellIndex, end: newFocusedCellIndex + 1 },
            selections: [{ start: newFocusedCellIndex, end: newFocusedCellIndex + 1 }]
        };
    }, undefined, true);
    notebookService.setToCopy(( (selectedCells.map(cell => cell.model))), false);
    return true;
}
let NotebookClipboardContribution = class NotebookClipboardContribution extends Disposable {
    static { this.ID = 'workbench.contrib.notebookClipboard'; }
    constructor(_editorService) {
        super();
        this._editorService = _editorService;
        const PRIORITY = 105;
        if (CopyAction) {
            this._register(CopyAction.addImplementation(PRIORITY, 'notebook-clipboard', accessor => {
                return this.runCopyAction(accessor);
            }));
        }
        if (PasteAction) {
            this._register(PasteAction.addImplementation(PRIORITY, 'notebook-clipboard', accessor => {
                return this.runPasteAction(accessor);
            }));
        }
        if (CutAction) {
            this._register(CutAction.addImplementation(PRIORITY, 'notebook-clipboard', accessor => {
                return this.runCutAction(accessor);
            }));
        }
    }
    _getContext() {
        const editor = getNotebookEditorFromEditorPane(this._editorService.activeEditorPane);
        const activeCell = editor?.getActiveCell();
        return {
            editor,
            activeCell
        };
    }
    _focusInsideEmebedMonaco(editor) {
        const windowSelection = getWindow(editor.getDomNode()).getSelection();
        if (windowSelection?.rangeCount !== 1) {
            return false;
        }
        const activeSelection = windowSelection.getRangeAt(0);
        if (activeSelection.startContainer === activeSelection.endContainer && activeSelection.endOffset - activeSelection.startOffset === 0) {
            return false;
        }
        let container = activeSelection.commonAncestorContainer;
        const body = editor.getDomNode();
        if (!body.contains(container)) {
            return false;
        }
        while (container
            &&
                container !== body) {
            if (container.classList && container.classList.contains('monaco-editor')) {
                return true;
            }
            container = container.parentNode;
        }
        return false;
    }
    runCopyAction(accessor) {
        const loggerService = accessor.get(ILogService);
        const activeElement = getActiveElement();
        if (activeElement instanceof HTMLElement && ['input', 'textarea'].indexOf(activeElement.tagName.toLowerCase()) >= 0) {
            _log(loggerService, '[NotebookEditor] focus is on input or textarea element, bypass');
            return false;
        }
        const { editor } = this._getContext();
        if (!editor) {
            _log(loggerService, '[NotebookEditor] no active notebook editor, bypass');
            return false;
        }
        if (!isAncestor(activeElement, editor.getDomNode())) {
            _log(loggerService, '[NotebookEditor] focus is outside of the notebook editor, bypass');
            return false;
        }
        if (this._focusInsideEmebedMonaco(editor)) {
            _log(loggerService, '[NotebookEditor] focus is on embed monaco editor, bypass');
            return false;
        }
        _log(loggerService, '[NotebookEditor] run copy actions on notebook model');
        return runCopyCells(accessor, editor, undefined);
    }
    runPasteAction(accessor) {
        const activeElement = getActiveElement();
        if (activeElement && ['input', 'textarea'].indexOf(activeElement.tagName.toLowerCase()) >= 0) {
            return false;
        }
        const notebookService = accessor.get(INotebookService);
        const pasteCells = notebookService.getToCopy();
        if (!pasteCells) {
            return false;
        }
        const { editor, activeCell } = this._getContext();
        if (!editor) {
            return false;
        }
        return runPasteCells(editor, activeCell, pasteCells);
    }
    runCutAction(accessor) {
        const activeElement = getActiveElement();
        if (activeElement && ['input', 'textarea'].indexOf(activeElement.tagName.toLowerCase()) >= 0) {
            return false;
        }
        const { editor } = this._getContext();
        if (!editor) {
            return false;
        }
        return runCutCells(accessor, editor, undefined);
    }
};
NotebookClipboardContribution = ( (__decorate([
    ( (__param(0, IEditorService)))
], NotebookClipboardContribution)));
registerWorkbenchContribution2(NotebookClipboardContribution.ID, NotebookClipboardContribution, 2 );
const COPY_CELL_COMMAND_ID = 'notebook.cell.copy';
const CUT_CELL_COMMAND_ID = 'notebook.cell.cut';
const PASTE_CELL_COMMAND_ID = 'notebook.cell.paste';
const PASTE_CELL_ABOVE_COMMAND_ID = 'notebook.cell.pasteAbove';
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: COPY_CELL_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 0, "Copy Cell")),
            menu: {
                id: MenuId.NotebookCellTitle,
                when: NOTEBOOK_EDITOR_FOCUSED,
                group: "1_copy" ,
                order: 2,
            },
            keybinding: isNative ? undefined : {
                primary: 2048  | 33 ,
                win: { primary: 2048  | 33 , secondary: [2048  | 19 ] },
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, ContextKeyExpr.not(InputFocusedContextKey)))),
                weight: 200
            }
        });
    }
    async runWithContext(accessor, context) {
        runCopyCells(accessor, context.notebookEditor, context.cell);
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: CUT_CELL_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 1, "Cut Cell")),
            menu: {
                id: MenuId.NotebookCellTitle,
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_EDITOR_EDITABLE, NOTEBOOK_CELL_EDITABLE))),
                group: "1_copy" ,
                order: 1,
            },
            keybinding: isNative ? undefined : {
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, ContextKeyExpr.not(InputFocusedContextKey)))),
                primary: 2048  | 54 ,
                win: { primary: 2048  | 54 , secondary: [1024  | 20 ] },
                weight: 200
            }
        });
    }
    async runWithContext(accessor, context) {
        runCutCells(accessor, context.notebookEditor, context.cell);
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: PASTE_CELL_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 2, "Paste Cell")),
            menu: {
                id: MenuId.NotebookCellTitle,
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_EDITOR_EDITABLE))),
                group: "1_copy" ,
                order: 3,
            },
            keybinding: isNative ? undefined : {
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, ContextKeyExpr.not(InputFocusedContextKey)))),
                primary: 2048  | 52 ,
                win: { primary: 2048  | 52 , secondary: [1024  | 19 ] },
                linux: { primary: 2048  | 52 , secondary: [1024  | 19 ] },
                weight: 100
            }
        });
    }
    async runWithContext(accessor, context) {
        const notebookService = accessor.get(INotebookService);
        const pasteCells = notebookService.getToCopy();
        if (!context.notebookEditor.hasModel() || context.notebookEditor.isReadOnly) {
            return;
        }
        if (!pasteCells) {
            return;
        }
        runPasteCells(context.notebookEditor, context.cell, pasteCells);
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: PASTE_CELL_ABOVE_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 3, "Paste Cell Above")),
            keybinding: {
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, ContextKeyExpr.not(InputFocusedContextKey)))),
                primary: 2048  | 1024  | 52 ,
                weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT
            },
        });
    }
    async runWithContext(accessor, context) {
        const notebookService = accessor.get(INotebookService);
        const pasteCells = notebookService.getToCopy();
        const editor = context.notebookEditor;
        const textModel = editor.textModel;
        if (editor.isReadOnly) {
            return;
        }
        if (!pasteCells) {
            return;
        }
        const originalState = {
            kind: SelectionStateType.Index,
            focus: editor.getFocus(),
            selections: editor.getSelections()
        };
        const currCellIndex = context.notebookEditor.getCellIndex(context.cell);
        const newFocusIndex = currCellIndex;
        textModel.applyEdits([
            {
                editType: 1 ,
                index: currCellIndex,
                count: 0,
                cells: ( (pasteCells.items.map(cell => cloneNotebookCellTextModel(cell))))
            }
        ], true, originalState, () => ({
            kind: SelectionStateType.Index,
            focus: { start: newFocusIndex, end: newFocusIndex + 1 },
            selections: [{ start: newFocusIndex, end: newFocusIndex + pasteCells.items.length }]
        }), undefined, true);
    }
});
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.toggleNotebookClipboardLog',
            title: ( localize2WithPath(_moduleId, 4, 'Toggle Notebook Clipboard Troubleshooting')),
            category: Categories.Developer,
            f1: true
        });
    }
    run(accessor) {
        toggleLogging();
        if (_logging) {
            const commandService = accessor.get(ICommandService);
            commandService.executeCommand(showWindowLogActionId);
        }
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: 'notebook.cell.output.selectAll',
            title: ( localizeWithPath(_moduleId, 5, "Select All")),
            keybinding: {
                primary: 2048  | 31 ,
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_OUTPUT_FOCUSED))),
                weight: NOTEBOOK_OUTPUT_WEBVIEW_ACTION_WEIGHT
            }
        });
    }
    async runWithContext(accessor, _context) {
        withEditor(accessor, editor => {
            if (!editor.hasEditorFocus()) {
                return false;
            }
            if (editor.hasEditorFocus() && !editor.hasWebviewFocus()) {
                return true;
            }
            const cell = editor.getActiveCell();
            if (!cell || !cell.outputIsFocused || !editor.hasWebviewFocus()) {
                return true;
            }
            if (cell.inputInOutputIsFocused) {
                editor.selectInputContents(cell);
            }
            else {
                editor.selectOutputContent(cell);
            }
            return true;
        });
    }
});

export { NotebookClipboardContribution, runCopyCells, runCutCells, runPasteCells };
